import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import papa, { ParseResult } from 'papaparse'
import { area, curveBumpX, easeBounceIn, scaleLinear, select } from 'd3'
import { years } from 'components/MainComponent/constants'
import { useElementSize, useWindowSize } from 'usehooks-ts'
import {
  party_colors,
  party_colors_text,
  party_colors_text_dm,
} from 'components/MainComponent/party_colors'
import { useTranslation } from 'react-i18next'

const minbarheight = 18
const minvalueheight = 6

export const HistoricalChartD3 = ({
  bfsid = 0,
  activeParty,
  activeYear,
  setActiveParty,
  setActiveYear,
  colorMode,
}: {
  bfsid?: number | string
  activeParty?: string
  activeYear?: number
  setActiveParty?: (e: string) => void
  setActiveYear?: (e: number) => void
  colorMode: 'dark' | 'light' | undefined
}) => {
  activeParty === 'Schweiz'
    ? (activeParty = undefined)
    : (activeParty = activeParty)
  const { t } = useTranslation()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const [drawData, setDrawData] = useState<YearData[]>()
  const [stackedData, setStackedData] = useState<{
    [key: string]: StackedData[]
  }>()
  const [hoveredPath, setHoveredPath] = useState<string | undefined>(
    activeParty
  )
  const [containerRef, containerSize] = useElementSize()
  const svgRef = useRef<SVGSVGElement>(null)
  const [highlightedYear, setHighlightedYear] = useState<number | undefined>(
    2019
  )

  const textcolor =
    colorMode == 'dark' ? party_colors_text_dm : party_colors_text

  useEffect(() => {
    setHoveredPath(activeParty)
  }, [activeParty])
  useEffect(() => {
    setHighlightedYear(activeYear)
  }, [activeYear])
  useEffect(() => {
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2023/nationalratswahlen23/data_municipality/by_gde_lps/' +
        bfsid +
        '.csv?a=' +
        Math.random()
    )
      .then((response) => response.text())
      .then((data) => {
        papa.parse<{}>(data, {
          header: true,
          delimiter: ',',
          dynamicTyping: true,
          complete: function (results: ParseResult<YearData>) {
            console.log(results.data)

            // Merge the Mitte parties
            let cvpData: any, bpdData: any, mitteData: any

            for (const item of results.data) {
              switch (item.party) {
                case 'CVP':
                  cvpData = item
                  break
                case 'BDP':
                  bpdData = item
                  break
                case 'Mitte':
                  mitteData = item
                  break
              }
            }

            const excludedKeys = ['2023', 'party']
            const newMitteData = Object.keys(mitteData)
              .filter((key) => !excludedKeys.includes(key))
              .reduce((accumulator, key) => {
                accumulator[key] =
                  (cvpData[key] as number) + (bpdData[key] as number)
                return accumulator
              }, {} as Record<string, number>)

            const { '2023': year2023 } = mitteData
            const newMitteDataAll = {
              ...newMitteData,
              '2023': year2023,
              party: 'Mitte',
            }
            const newResult = [
              ...results.data.filter((r) => !['Mitte', 'CVP', 'BDP'].includes(r.party as string)),
              newMitteDataAll,
            ]
            console.log(newResult)
            // End of merge

            setDrawData(newResult as any)
          },
        })
      })
  }, [bfsid])
  const chartHeight = isMobile ? 300 : 400
  const drawSpace = {
    left: 15,
    right: containerSize.width - 40,
    top: 0,
    bottom: containerSize.height - (isMobile ? 30 : 20),
  }
  const x = useMemo(
    () =>
      scaleLinear<number, number>()
        .domain([years[0], years[years.length - 1]])
        .range([drawSpace.left, drawSpace.right]),
    [years, containerSize, windowSize.width]
  )

  const y = useMemo(
    () =>
      scaleLinear<number, number>()
        .domain([0, 100])
        .range([0, chartHeight * (isMobile ? 0.85 : 0.83)]),
    [containerSize, windowSize.width]
  )

  useMemo(() => {
    if (!drawData) return
    const by_year: { [key: string]: any } = {}
    years.map((year) => {
      if (!by_year[year]) by_year[year] = []
      drawData.map((element) => {
        by_year[year].push([element.party as string, element[year] as number])
      })
      by_year[year] = by_year[year].sort(
        (a: [string, number], b: [string, number]) => a[1] - b[1]
      )
    })

    let currenty = 3
    let stack: { [key: number]: StackedData[] } = {}
    let stacked_by_party: { [key: string]: StackedData[] } = {}
    Object.entries(by_year).forEach(([year, parties]) => {
      currenty = 0
      stack[+year] = []
      parties = parties.map((m: [string, number]) => {
        var height = y(m[1])
        var bottom = currenty
        var top = currenty + height
        currenty += height + (isMobile ? 2 : 5)
        if (!stacked_by_party[m[0]]) stacked_by_party[m[0]] = []
        stacked_by_party[m[0]].push({
          year: +year,
          party: m[0],
          value: m[1],
          height,
          bottom,
          top,
        })
        stack[+year].push({
          year: +year,
          party: m[0],
          value: m[1],
          height,
          bottom,
          top,
        })
        return { year, party: m[0], value: m[1], height, bottom, top }
      })
    })

    setStackedData(stacked_by_party)

    Object.entries(stack).map((m) => {})
  }, [drawData, windowSize.width])

  function pathFill(d: any) {
    return party_colors[d[0]]
  }
  function pathOpacity(d: any) {
    if (hoveredPath && hoveredPath !== d[0]) {
      return 0.25
    }
    return 0.85
  }

  function textFill(d: any) {
    if (hoveredPath && hoveredPath !== d[0]) {
      return 'var(--brandblue-main)'
    }
    return colorMode == 'dark' ? '#fff' : '#202346'
  }

  function transitionSettings(transition: any) {
    transition.duration(200).delay(100)
  }
  const last_year_rect_width = windowSize.width < 712 ? 13 : 27
  useEffect(() => {
    if (!stackedData || !svgRef.current) return

    let svg = select(svgRef.current)

    svg
      .select('.viz')
      .selectAll('path')
      .data(
        Object.entries(stackedData).sort((a, b) => a[0].localeCompare(b[0]))
      )
      .join(
        (enter) =>
          enter
            .append('path')
            .on('mouseenter', (d) =>
              windowSize.width > 712
                ? setHoveredPath(d.target.classList[1])
                : undefined
            )
            // .on('click', (d) => {
            //   setActiveParty && setActiveParty(d.target.classList[1])
            //   setHoveredPath(d.target.classList[1])
            // })

            .on(
              'mouseleave',
              isMobile
                ? () => {}
                : () =>
                    setHoveredPath(
                      activeParty === 'Schweiz' ? undefined : activeParty
                    )
            )

            .attr('class', (d) => 'stackedpath ' + d[0])
            .attr('d', (d) => shape(d[1] as any) as any)
            .attr('fill', pathFill)
            .attr('opacity', pathOpacity),
        (update) =>
          update
            .on('mouseenter', (d) =>
              windowSize.width > 712
                ? setHoveredPath(d.target.classList[1])
                : undefined
            )
            .on(
              'mouseleave',
              isMobile
                ? () => {}
                : () =>
                    setHoveredPath(
                      activeParty === 'Schweiz' ? undefined : activeParty
                    )
            )
            .transition()
            .call(transitionSettings)
            .attr('d', (d) => shape(d[1] as any) as any)
            .attr('fill', pathFill)
            .attr('opacity', pathOpacity)
      )

    // svg
    //   .select('.bar')
    //   .selectAll('rect')
    //   .data(
    //     Object.entries(stackedData)
    //       .map((m) => {
    //         m[1] = m[1].filter(
    //           (f) => f.year === (activeYear || years[years.length - 1])
    //         )
    //         return m
    //       })
    //       .sort((a, b) => a[0].localeCompare(b[0]))
    //   )
    //   .join(
    //     (enter) =>
    //       enter
    //         .append('rect')
    //         .attr('class', (d) => 'bar ' + d[0])
    //         .attr('x', (d) => x(d[1][0].year) - last_year_rect_width)
    //         .attr(
    //           'y',
    //           (d) => drawSpace.bottom - d[1][0].bottom - d[1][0].height
    //         )
    //         .on(
    //           'mouseenter',
    //           isMobile ? () => {} : (d) => setHoveredPath(d.target.classList[1])
    //         )
    //         .on('click', (d) => setHoveredPath(d.target.classList[1]))
    //         .on(
    //           'mouseleave',
    //           isMobile
    //             ? () => {}
    //             : (e) =>
    //                 setHoveredPath(
    //                   activeParty === 'Schweiz' ? undefined : activeParty
    //                 )
    //         )
    //         .attr('width', last_year_rect_width * 2)
    //         .attr('height', (d) => d[1][0].height)
    //         .attr('fill', pathFill)
    //         .attr('opacity', pathOpacity),
    //     (update) =>
    //       update
    //         .on(
    //           'mouseenter',
    //           isMobile ? () => {} : (d) => setHoveredPath(d.target.classList[1])
    //         )
    //         .on('click', (d) => setHoveredPath(d.target.classList[1]))
    //         .on('mouseleave', () =>
    //           setHoveredPath(
    //             activeParty === 'Schweiz' ? undefined : activeParty
    //           )
    //         )
    //         .transition()
    //         .call(transitionSettings)
    //         .attr(
    //           'y',
    //           (d) => drawSpace.bottom - d[1][0].bottom - d[1][0].height
    //         )

    //         .attr(
    //           'x',
    //           (d) => x(activeYear || d[1][0].year) - last_year_rect_width
    //         )
    //         .attr('width', last_year_rect_width * 2)
    //         .attr('height', (d) => d[1][0].height)
    //         .attr('fill', pathFill)
    //         .attr('opacity', pathOpacity)
    //   )

    function labelForActiveYear(
      stackedData: { [key: string]: any[] },
      prop: (e: any) => string,
      group: string,
      yOffset: (e: any) => number
    ) {
      svg
        .select('.' + group)
        .selectAll('text')
        .data(
          Object.entries(stackedData).map((m) => {
            m[1] = m[1].filter((f) => f.year === 2023)
            return m
          })
        )
        .join(
          (enter) =>
            enter
              .append('text')
              .attr('text-anchor', 'staret')
              .attr('x', (d) => x(2023) + 10)
              .attr(
                'y',
                (d) =>
                  drawSpace.bottom -
                  d[1][0].bottom -
                  d[1][0].height / 2 +
                  yOffset(d[1][0])
              )
              .text((d) => prop(d[1][0]))
              .attr('fill', textFill),
          (update) =>
            update
              .html('')
              .text((d) => prop(d[1][0]))
              .transition()
              .call(transitionSettings)
              .attr('x', (d) => x(2023) + 10)
              .attr(
                'y',
                (d) =>
                  drawSpace.bottom -
                  d[1][0].bottom -
                  d[1][0].height / 2 +
                  yOffset(d[1][0])
              )
              .attr('height', (d) => d[1][0].height)
              .attr('fill', textFill)
        )
    }
    if (stackedData) {
      labelForActiveYear(
        stackedData,
        (d: any) => (d.height > minbarheight ? t(`swissmap:parties:${d.party}`) : ''),
        'barlabels',
        (d: any) => -2
      )
      labelForActiveYear(
        stackedData,
        (d: any) =>
          (d.height > minbarheight || hoveredPath) && d.height > minvalueheight
            ? windowSize.width < 712
              ? (+d.value).toFixed(1)
              : (+d.value).toFixed(1)
            : '',
        'barvalues',
        (d: any) =>
          d.height <= minbarheight
            ? windowSize.width < 712
              ? 4
              : 4
            : windowSize.width < 712
            ? 11
            : 11
      )
    }

    // svg
    //   .select('.yearHighlight')
    //   .selectAll('rect')
    //   .data([activeYear || 0])
    //   .join(
    //     (enter) =>
    //       enter
    //         .append('rect')
    //         .attr('width', last_year_rect_width * 2)
    //         .attr('height', drawSpace.bottom + 2)
    //         .attr(
    //           'x',
    //           (d) =>
    //             x(d) -
    //             (yearSelectionWidth / 2 - (windowSize.width < 712 ? 0 : 2.5))
    //         )
    //         .attr('y', drawSpace.top + 4)
    //         .attr('fill', 'var(--tooltip-background)')
    //         .attr('stroke', 'var(--tooltip-background)')
    //         .attr('stroke-width', 8)
    //         .attr('filter', 'drop-shadow(0px 0px 4px rgb(0 0 0 / 0.4))'),
    //     (update) =>
    //       update
    //         .attr('width', last_year_rect_width * 2)
    //         .attr('height', drawSpace.bottom + 2)

    //         .attr('fill', 'var(--tooltip-background)')
    //         .attr('stroke', 'var(--tooltip-background)')
    //         .attr('stroke-width', 8)
    //         .attr('filter', 'drop-shadow(0px 0px 4px rgb(0 0 0 / 0.4))')

    //         .transition()
    //         .call(transitionSettings)

    //         .attr(
    //           'x',
    //           (d) =>
    //             x(d) -
    //             yearSelectionWidth / 2 -
    //             (windowSize.width < 712 ? 0 : 2.5)
    //         )
    //         .attr('y', drawSpace.top + 4)
    //   )

    const labelData = Object.entries(stackedData)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .filter((f) => f[0] === hoveredPath)
    svg
      .select('.valuelabels')
      .selectAll('text')
      .data(labelData.length ? (labelData[0][1] as StackedData[]) : [])
      .join(
        (enter) =>
          enter
            .append('text')
            .attr('class', (d) => 'valuelabelRect ')
            .text((d) =>
              d.value ? +d.value.toFixed(1).replace('.', ',') + '%' : '.'
            )
            .attr('x', (d) => x(d.year) + 5)
            .attr('y', (d) => drawSpace.bottom - d.bottom - d.height / 2 + 5)
            .attr('text-anchor', 'middle')
            .attr('pointer-events', 'none')
            .attr('user-select', 'none')
            .attr('class', (d) => 'valuelabel ' + d.party)
            .text((d) => d.value)
            .attr('fill', (d) =>
              d.year === 2023   ||
              d.height === 0 ||
              !hoveredPath ||
              (isMobile && d.year !== activeYear)
                ? 'transparent'
                : '#fff'
            )
            .attr('stroke', (d) =>
              d.height === 0 ||
              !hoveredPath ||
              (isMobile && d.year !== activeYear)
                ? 'transparent'
                : party_colors[d.party]
            )
            .attr('stroke-width', 4)
            .attr('stroke-linejoin', 'round')
            .attr('paint-order', 'stroke fill'),

        (update) =>
          update

            .transition()
            .call(transitionSettings)
            .ease(easeBounceIn)
            .attr('x', (d) => x(d.year) + 5)
            .attr('y', (d) => drawSpace.bottom - d.bottom - d.height / 2)

            .text((d) => (d.value ? d.value.toFixed(1).replace('.', ',') : '.'))
            .attr('fill', (d) =>
              d.year === 2023 ||
              d.height === 0 ||
              !hoveredPath ||
              (isMobile && d.year !== highlightedYear)
                ? 'transparent'
                : '#fff'
            )
            .attr('stroke', (d) =>
              d.year === 2023  ||
              d.height === 0 ||
              !hoveredPath ||
              (isMobile && d.year !== highlightedYear)
                ? 'transparent'
                : party_colors[d.party]
            ),
        (exit) =>
          exit
            .transition()
            .call(transitionSettings)
            .text((d) => (d.value ? d.value.toFixed(1).replace('.', ',') : '.'))
            .attr('fill', 'transparent')
            .attr('stroke', 'transparent')
      )
  }, [
    stackedData,
    svgRef.current,
    containerSize.width,
    windowSize.width,
    hoveredPath,
    activeParty,
    activeYear,
    bfsid,
    t,
  ])

  const shape = area<StackedData>()
    .x((d) => x(d.year))
    .y0((d) => drawSpace.bottom - d.bottom)
    .y1((d) => drawSpace.bottom - d.top)
    .curve(curveBumpX)

  const yearSelectionWidth = isMobile ? 25 : 50
  return (
    <HistoricalChartRoot
      ref={containerRef}
      style={{ height: chartHeight + 'px' }}
    >
      <ChartSVG ref={svgRef}>
        <XAxis>
          {years.map((year, i) => {
            return (
              <g
                transform={`translate(${x(year)},${drawSpace.bottom + 10})`}
                key={'years_' + i}
              >
                <YearSelection
                  x={-yearSelectionWidth / 2}
                  y={activeYear === year ? 1 : 0}
                  width={yearSelectionWidth}
                  height={100}
                  fontWeight={activeYear === year ? 700 : 300}
                  fontSize={activeYear === year ? 14 : 12}
                  cursor={'pointer'}
                  onClick={() => {
                    setActiveYear && setActiveYear(year)
                  }}
                >
                  <div>{isMobile ? (year + '').substring(2) : year}</div>
                </YearSelection>
              </g>
            )
          })}
        </XAxis>
        <g className='bar_left' transform='translate(-100,0)'></g>
        <g>
          {years.map((year, i) => {
            return (
              <line
                key={'xtick_' + i}
                x1={x(year)}
                x2={x(year)}
                y1={0}
                y2={drawSpace.bottom + 10}
                stroke={
                  year === highlightedYear
                    ? 'var(--brandblue-main)'
                    : 'rgba(0,0,0,.2)'
                }
                strokeWidth={year === highlightedYear ? 1.5 : 1.5}
              />
            )
          })}
        </g>
        <g className='viz'></g>
        <g className='valuelabels'></g>
        <g className='yearHighlight'></g>
        {/* <g className='bar'></g> */}
        <g className='barlabels'></g>
        <g className='barvalues'></g>
      </ChartSVG>
      <ColorKey>
        {Object.entries(party_colors).map(([name, color]) => {
          if (['CVP', 'BDP', 'neutral'].includes(name)) return
          return (
            <KeyEntry>
              <Swatch style={{ backgroundColor: color }} />
              <div>{t(`swissmap:parties:${name}`)}</div>
            </KeyEntry>
          )
        })}
      </ColorKey>
      <Note>{t('swissmap:centerNote')}</Note>
    </HistoricalChartRoot>
  )
}

const Note = styled.div`
  font-size: 12px;
  line-height: 1.2em;
  margin-top: 10px;
  text-align: center;
`

const Deselect = styled.foreignObject`
  width: 100px;
  height: 20px;
  background-color: #f00;
`

const YearSelection = styled.foreignObject`
  div {
    width: 100%;
    text-align: center;
  }
`

const ColorKey = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  /* width: calc(100% - 50px); */
  line-height: 1.6em;
  /* justify-content: space-between; */
  font-size: 12px;
`
const KeyEntry = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
`
const Swatch = styled.div`
  width: 10px;
  height: 10px;
  background-color: var(--red-main);
  margin-right: 5px;
`
const HistoricalChartRoot = styled.div`
  width: 100%;
  margin-bottom: 80px;
  margin-top: 30px;
`
const ChartSVG = styled.svg`
  width: 100%;
  height: 100%;
  overflow: visible;
  font-family: var(--font-sans-serif);
  margin-bottom: 10px;
  .barlabels {
    text {
      pointer-events: none;
      user-select: none;
      font-size: 12px;
      font-weight: 700;
    }
  }
  .barvalues {
    text {
      pointer-events: none;
      user-select: none;
      font-weight: 300;
      font-size: 12px;
    }
  }
  .valuelabel {
    pointer-events: none;
    font-weight: 700;
    font-size: 12px;
  }
  .valuelabelRect {
    pointer-events: none;
  }
  .percent {
    font-weight: 300;
  }
`

const XAxis = styled.g`
  font-size: 12px;
`
